import { makeAutoObservable, runInAction } from 'mobx'
import axios from 'axios'
import { toast } from 'react-toastify'
import { resolveApiBaseUrl } from '../api/resolveApiBaseUrl'
import type { ShootDto } from '../models/shoot'
import type EditorStore from './EditorStore'

export type UploadDrawerStep = 'options' | 'preview' | 'editor' | 'details';
export type UploadMediaType = 'photo' | 'video';

const MAX_VIDEO_BYTES = 100 * 1024 * 1024

class UploadDrawerStore {
  isOpen = false
  step: UploadDrawerStep = 'options'
  selectedFile: File | null = null
  mediaType: UploadMediaType = 'photo'
  /** Blob/object URL for preview; revoke on reset. */
  previewUrl: string | null = null
  isUploading = false
  uploadProgress = 0
  error: string | null = null

  constructor (private readonly editorStore: EditorStore) {
    makeAutoObservable(this)
  }

  open = () => {
    runInAction(() => {
      this.isOpen = true
    })
  }

  close = () => {
    this.reset()
    runInAction(() => {
      this.isOpen = false
    })
  }

  reset = () => {
    if (this.previewUrl) {
      try {
        URL.revokeObjectURL(this.previewUrl)
      } catch {
        /* ignore */
      }
    }
    runInAction(() => {
      this.step = 'options'
      this.selectedFile = null
      this.mediaType = 'photo'
      this.previewUrl = null
      this.isUploading = false
      this.uploadProgress = 0
      this.error = null
    })
    this.editorStore.reset()
  }

  clearError = () => {
    runInAction(() => {
      this.error = null
    })
  }

  setErrorMessage = (message: string) => {
    runInAction(() => {
      this.error = message
    })
  }

  setStep = (step: UploadDrawerStep) => {
    runInAction(() => {
      this.step = step
    })
  }

  backToOptions = () => {
    if (this.previewUrl) {
      try {
        URL.revokeObjectURL(this.previewUrl)
      } catch {
        /* ignore */
      }
    }
    runInAction(() => {
      this.step = 'options'
      this.selectedFile = null
      this.previewUrl = null
      this.mediaType = 'photo'
      this.error = null
    })
  }

  backToPreview = () => {
    runInAction(() => {
      this.step = 'preview'
      this.error = null
    })
  }

  selectFile = (file: File, type: UploadMediaType) => {
    this.clearError()
    if (type === 'video' && file.size > MAX_VIDEO_BYTES) {
      runInAction(() => {
        this.error = 'Video too large'
      })
      return
    }
    if (this.previewUrl) {
      try {
        URL.revokeObjectURL(this.previewUrl)
      } catch {
        /* ignore */
      }
    }
    const url = URL.createObjectURL(file)
    runInAction(() => {
      this.selectedFile = file
      this.mediaType = type
      this.previewUrl = url
      this.step = 'preview'
    })
  }

  goToEditor = () => {
    runInAction(() => {
      this.step = 'editor'
      this.error = null
    })
  }

  goToDetails = () => {
    runInAction(() => {
      this.step = 'details'
      this.error = null
    })
  }

  backToEditor = () => {
    runInAction(() => {
      this.step = 'editor'
      this.error = null
    })
  }

  /**
   * POST /api/shoots — empty text boxes for stub; ShootsEditor posts its own when flag on.
   */
  uploadShoot = async (textBoxesJson = '[]') => {
    if (!this.selectedFile) {
      runInAction(() => {
        this.error = 'No file selected.'
      })
      return
    }
    if (this.isUploading) return

    runInAction(() => {
      this.isUploading = true
      this.uploadProgress = 0
      this.error = null
    })

    const formData = new FormData()
    formData.append('mediaFile', this.selectedFile)
    formData.append('mediaType', this.mediaType)
    formData.append('textBoxesJson', textBoxesJson)

    try {
      await axios.post<ShootDto>(`${resolveApiBaseUrl()}/shoots`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => {
          if (ev.total) {
            const pct = Math.round((ev.loaded / ev.total) * 100)
            runInAction(() => {
              this.uploadProgress = pct
            })
          }
        }
      })

      runInAction(() => {
        this.isUploading = false
        this.uploadProgress = 100
      })

      const { store } = await import('./store')
      void store.slopFeedStore.fetchFeedItems(store.slopFeedStore.defaultLimit, null)
      void store.feedStore.loadTrending(10, 7)
      void store.feedStore.loadFollowingFeed(20)

      toast.success('Shoot posted. Open Slop Feed → Discover to see it in the mixed feed.')
      this.close()
    } catch (err: unknown) {
      let message = 'Upload failed'
      const ax = err as { response?: { data?: { detail?: string } }; message?: string }
      if (ax?.response?.data?.detail) message = String(ax.response.data.detail)
      else if (ax?.message) message = ax.message
      runInAction(() => {
        this.error = message
        this.isUploading = false
        this.uploadProgress = 0
      })
    }
  }

  /** Called when ShootsEditor finishes POST /shoots successfully. */
  afterSuccessfulShootFromEditor = async () => {
    const { store } = await import('./store')
    void store.slopFeedStore.fetchFeedItems(store.slopFeedStore.defaultLimit, null)
    void store.feedStore.loadTrending(10, 7)
    void store.feedStore.loadFollowingFeed(20)
    toast.success('Shoot posted. Open Slop Feed → Discover to see it in the mixed feed.')
    this.close()
  }
}

export default UploadDrawerStore
